import { useState } from 'react'

import {
  useLazyListContractsQuery,
  useGetOneFatContractQuery, // this one is the for initial load, which happens before the ui renders anything
  useUpsertContractMutation,
  useBulkUpdateContractsMutation,
  useDeleteContractMutation
} from '../../apiSlice/contractApi'

import useSearchCallback from '../../apiSlice/useSearchCallback'
import useFetchAuxiliaryLists from '../../apiSlice/useFetchAuxiliaryLists'
import { BucketConfig } from '../../../sharedcomponents/5templates/detailCards/BucketConfig'
import { RootObjectTypes } from '../../../common/globalNotary'
import { useNavigate } from 'react-router-dom'

const useContract = () => {
  const [loadContracts] = useLazyListContractsQuery()
  const [deleteAccount] = useDeleteContractMutation()
  const [bulkUpdateContractsCallBack] = useBulkUpdateContractsMutation()
  const navigate = useNavigate()

  const [upsertContractRecordCallback] = useUpsertContractMutation()

  const {
    searchEntitiesCallback,
    searchPartiesCallback,
    searchNonContainerAssetsCallback,
    searchServicesCallback
  } = useSearchCallback()

  const [finalAuxiliaryRecords, setFinalAuxiliaryRecords] = useState(null)
  const [loading, setLoading] = useState(false)

  const constructAndSetFinalListBucket = (
    completeResponse,
    primaryContractRecord
  ) => {
    const finalListBuckets = {}
    finalListBuckets.primero = [
      new BucketConfig(
        completeResponse[0].data,
        searchPartiesCallback,
        'Contacts',
        null,
        'genericSelect',
        'decorated',
        {
          controlArray: [
            {
              type: 'select',
              baseName: 'fullContacts[INDEX].role_id',
              optionsList: completeResponse[5].data
                .map(o => {
                  o.value = o.id
                  o.text = o.name
                  return o
                })
                .filter(o => o.type === 'contact'),
              disabled: false
            }
          ]
        }
      ),
      new BucketConfig(
        completeResponse[1].data,
        searchPartiesCallback,
        'Owners',
        null,
        'genericSelect',
        'decorated',
        {
          controlArray: [
            {
              type: 'select',
              baseName: 'fullOwners[INDEX].role_id',
              optionsList: completeResponse[5].data
                .map(o => {
                  o.value = o.id
                  o.text = o.name
                  return o
                })
                .filter(o => o.type === 'owner'),
              disabled: false
            }
          ]
        }
      )
    ]

    finalListBuckets.segundo = [
      new BucketConfig(
        completeResponse[2].data,
        searchEntitiesCallback,
        'Entities',
        null
      )
    ]
    finalListBuckets.tercero = [
      new BucketConfig(
        completeResponse[3].data,
        searchNonContainerAssetsCallback,
        'Locations',
        null
      ),

      new BucketConfig(
        completeResponse[4].data,
        searchServicesCallback,
        'Services',
        null
      )
    ]

    setFinalAuxiliaryRecords(finalListBuckets)
  }

  const {
    constructValueAsPromiseCall,
    constructGetAllContractRolesCall,
    constructServicesByIdArrayCall,
    constructEntitiesByIdArrayCall,
    constructAssetsByIdArrayCall
  } = useFetchAuxiliaryLists()

  const fetchAuxiliaryRecords = async primaryContractRecord => {
    const equalityEvaluator = (a, b) => {
      return a.id === b.id && a.base_type === b.base_type
    }

    const fetchCallArray = [
      constructValueAsPromiseCall(
        'fullContacts',
        primaryContractRecord.fullContacts,
        ['role_id'],
        equalityEvaluator
      ),
      constructValueAsPromiseCall(
        'fullOwners',
        primaryContractRecord.fullOwners,
        ['role_id'],
        equalityEvaluator
      ),

      constructEntitiesByIdArrayCall(primaryContractRecord, 'entity_ids'),
      constructAssetsByIdArrayCall(primaryContractRecord, 'asset_ids'),
      constructServicesByIdArrayCall(primaryContractRecord, 'service_ids'),
      constructGetAllContractRolesCall()
    ]

    /// this Promise.all will not reject, errors come back on the error object,
    /// if everything works out, the error object is null or undefined
    Promise.all(fetchCallArray.map(f => f.call()))
      .then(res => {
        constructAndSetFinalListBucket(res, primaryContractRecord)
      })
      .catch(console.error)
  }

  const makeInMemoryContractRecord = type => {
    return {
      name: '',
      description: '',
      starts_at: new Date().toDateString(),
      expires_at: new Date().toDateString(),
      url: '',
      media_ids: [],
      service_ids: [],
      party_ids: [],
      entity_ids: [],
      asset_ids: [],
      fullContacts: [],
      fullOwners: [],
      owners: [],
      contacts: [],
      active: true,
      _objectId: ''
    }
  }

  const removeAccount = async id => {
    deleteAccount({ id }).then(() => {
      navigate(-1)
    }).catch(err => {
      console.error(err)
      alert('Could not delete account. Please check that other objects do not depend on it.')
    })
  }

  const suspendAccount = async (id, active) => {
    bulkUpdateContractsCallBack([
      { id, suspended_at: active ? null : new Date() }
    ])
  }

  const upsertContractCallback = primaryRecord => {
    setLoading(true);
    let purgedPrimaryRecord = primaryRecord

    purgedPrimaryRecord = Object.keys(purgedPrimaryRecord).filter(objKey =>
      objKey !== '__permissions__').reduce((newObj, key) =>
        {
          newObj[key] = purgedPrimaryRecord[key]
          return newObj
        }, {}
    )

    const returnPromise = new Promise((resolve, reject) => {
      upsertContractRecordCallback(purgedPrimaryRecord)
        .unwrap()
        .then(primaryResult => {
          const id = primaryResult.data.id
            ? primaryResult.data.id
            : primaryResult.data.data.id

          resolve({ id, type: RootObjectTypes.ACCOUNT })
        })
        .catch(e => {
          reject(e)
        })
        .finally(() => {
          setLoading(false);
        });
    })

    return returnPromise
  }

  return {
    loadContracts,
    bulkUpdateContractsCallBack,
    useGetOneFatContractQuery,
    fetchAuxiliaryRecords,
    makeInMemoryContractRecord,
    upsertContractCallback,
    removeAccount,
    suspendAccount,
    finalAuxiliaryRecords,
    searchServicesCallback,
    loading
  }
}

export default useContract
